<template>
  <div :class="isTitleVisible ? 'p-grid' : ''">
    <Card :class="['style', 'p-col', { 'embedded-alarm-table': !isTitleVisible }]" style="width: 100%">

      <!-- Full title bar: only rendered in standalone mode -->
      <template v-if="isTitleVisible" #title>
        <div class="p-d-flex p-jc-between">
          <div>
            <h3 class="p-m-0">
              <strong>{{ pageTitle }}</strong>
            </h3>
            <MultiSelectableBreadcrumb
                v-if="orgFilter === null"
                ref="breadcrumbData"
                :singleSelection="false">
            </MultiSelectableBreadcrumb>
          </div>
          <div>
            <Button
                v-tooltip.left="_t('primefaces.datatable.SORT_LABEL')"
                class="p-button-rounded p-button-success p-button-text"
                icon="pi pi-sort"
                style="font-size: 16px;"
                @click="toggleSort"
            />
            <OverlayPanel ref="srt">
              <div class="p-field">
                <label for="sortKey"> {{ _t('primefaces.datatable.SORT_LABEL') }} </label>
                <br>
                <Dropdown
                    id="sortKey"
                    v-model="sortKey"
                    :disabled="orderByPriority"
                    :options="alarmTableFilterKeys"
                    optionLabel="label"
                    optionValue="value" style="margin: 0px;"
                >
                </Dropdown>
              </div>
              <div class="p-field-checkbox">
                <Checkbox id="orderByPriority" v-model="orderByPriority" :binary="true"
                          :disabled="sortKey !== ''"></Checkbox>
                <label for="orderByPriority">{{ _t('filter_sorted_prio') }}</label>
              </div>
            </OverlayPanel>
            <ToggleButton
                v-model="groupFilter"
                v-tooltip.bottom="_t('label_Display_filters')"
                class="p-button-rounded p-button-text p-button-plain"
                offIcon="pi pi-filter"
                onIcon="pi pi-filter"
                style="font-size: 16px;"
            />
            <Button
                class="p-button-rounded p-button-text p-button-success mb-2"
                icon="pi pi-file-pdf"
                style="font-size: 16px;"
                @click="togglePDFDownload"
            />
            <OverlayPanel ref="pdfDownload">
              <div v-for="downloadItem in downloadPdfItems"
                   v-bind:key="downloadItem.label">
                <Button
                    :icon="downloadItem.icon"
                    :label="downloadItem.label"
                    class="p-button-rounded p-button-success p-button-text"
                    style="font-size: 16px;" @click="getFile(downloadItem.type)"
                />
              </div>
            </OverlayPanel>
            <Button
                class="p-button-rounded p-button-success p-button-text"
                icon="pi pi-file-excel"
                style="font-size: 16px;"
                @click="toggleExcelDownload"
            />
            <OverlayPanel ref="xlsDownload">
              <div v-for="downloadItem in downloadExcelItems"
                   v-bind:key="downloadItem.label">
                <Button
                    :icon="downloadItem.icon"
                    :label="downloadItem.label"
                    class="p-button-rounded p-button-success p-button-text"
                    style="font-size: 16px;" @click="getFile(downloadItem.type)"
                />
              </div>
            </OverlayPanel>
          </div>
        </div>
      </template>

      <template #content>
        <!-- Compact controls bar when embedded (isTitleVisible=false) -->
        <div v-if="!isTitleVisible" class="p-d-flex p-jc-end" style="margin-bottom: 0.25rem;">
          <Button
              v-tooltip.left="_t('primefaces.datatable.SORT_LABEL')"
              class="p-button-rounded p-button-success p-button-text"
              icon="pi pi-sort"
              style="font-size: 16px;"
              @click="toggleSort"
          />
          <OverlayPanel ref="srt">
            <div class="p-field">
              <label for="sortKey"> {{ _t('primefaces.datatable.SORT_LABEL') }} </label>
              <br>
              <Dropdown
                  id="sortKey"
                  v-model="sortKey"
                  :disabled="orderByPriority"
                  :options="alarmTableFilterKeys"
                  optionLabel="label"
                  optionValue="value" style="margin: 0px;"
              >
              </Dropdown>
            </div>
            <div class="p-field-checkbox">
              <Checkbox id="orderByPriority" v-model="orderByPriority" :binary="true"
                        :disabled="sortKey !== ''"></Checkbox>
              <label for="orderByPriority">{{ _t('filter_sorted_prio') }}</label>
            </div>
          </OverlayPanel>
          <ToggleButton
              v-model="groupFilter"
              v-tooltip.bottom="_t('label_Display_filters')"
              class="p-button-rounded p-button-text p-button-plain"
              offIcon="pi pi-filter"
              onIcon="pi pi-filter"
              style="font-size: 16px;"
          />
          <Button
              class="p-button-rounded p-button-text p-button-success"
              icon="pi pi-file-pdf"
              style="font-size: 16px;"
              @click="togglePDFDownload"
          />
          <OverlayPanel ref="pdfDownload">
            <div v-for="downloadItem in downloadPdfItems"
                 v-bind:key="downloadItem.label">
              <Button
                  :icon="downloadItem.icon"
                  :label="downloadItem.label"
                  class="p-button-rounded p-button-success p-button-text"
                  style="font-size: 16px;" @click="getFile(downloadItem.type)"
              />
            </div>
          </OverlayPanel>
          <Button
              class="p-button-rounded p-button-success p-button-text"
              icon="pi pi-file-excel"
              style="font-size: 16px;"
              @click="toggleExcelDownload"
          />
          <OverlayPanel ref="xlsDownload">
            <div v-for="downloadItem in downloadExcelItems"
                 v-bind:key="downloadItem.label">
              <Button
                  :icon="downloadItem.icon"
                  :label="downloadItem.label"
                  class="p-button-rounded p-button-success p-button-text"
                  style="font-size: 16px;" @click="getFile(downloadItem.type)"
              />
            </div>
          </OverlayPanel>
        </div>

        <TabView
            v-if="isTitleVisible"
            v-model:activeIndex="activeTab"
            @tab-click="switchTab()"
        >
          <TabPanel :header="_t('label_alarmtable')">
            <Alarms
                ref="reportAll"
                v-if="activeTab === 0"
                :isFirstSelected="isFirstSelected"
                :activeTab="0"
                :alarmTableValues="alarmTableValues"
                :group-filter="groupFilter"
                :org-filter="effectiveOrgFilter"
                filters-prefix="all"
            />
          </TabPanel>
          <TabPanel :header="_t('menu.my_alarms')">
            <Alarms
                ref="myAlarms"
                v-if="activeTab === 1"
                :isFirstSelected="isFirstSelected"
                :activeTab="1"
                :alarmTableValues="alarmTableValues"
                :group-filter="groupFilter"
                :org-filter="effectiveOrgFilter"
                :sort-key="sortKey"
                filters-prefix="my"
            />
          </TabPanel>
          <!--          <TabPanel-->
          <!--              :disabled="true"-->
          <!--              :header="_t('menu.manual_alarms')"-->
          <!--              filters-prefix="manual"-->
          <!--          />-->
          <TabPanel :header="_t('label_history')">
            <Alarms
                ref="reportHistory"
                v-if="activeTab === 2"
                :isFirstSelected="isFirstSelected"
                :activeTab="2"
                :alarmTableValues="alarmTableValues"
                :group-filter="groupFilter"
                :org-filter="effectiveOrgFilter"
                filters-prefix="history"
            />
          </TabPanel>
          <TabPanel
              :disabled="true"
              :header="_t('menu.checkin_checkout')"
              filters-prefix="checkin"
          />
          <TabPanel
              :disabled="true"
              :header="_t('ALARM_MONITORING')"
              filters-prefix="monitor"
          />
          <!--          <TabPanel-->
          <!--              :disabled="true"-->
          <!--              :header="_t('label_APP_Lone_worker')"-->
          <!--              filters-prefix="monitor"-->
          <!--          >-->
          <!--            <LoneworkerHistory/>-->
          <!--          </TabPanel>-->
          <!--          <TabPanel-->
          <!--              :disabled="true"-->
          <!--              :header="_t('label_APP_Lone_worker_active')"-->
          <!--              filters-prefix="monitor"-->
          <!--          >-->
          <!--            <LoneworkerActive/>-->
          <!--          </TabPanel>-->
          <!--          <TabPanel-->
          <!--              :disabled="true"-->
          <!--              :header="_t('Selection')"-->
          <!--              filters-prefix="monitor"-->
          <!--          >-->
          <!--            <Selection/>-->
          <!--          </TabPanel>-->
        </TabView>
        <Alarms
            v-else
            ref="reportAll"
            :isFirstSelected="isFirstSelected"
            :activeTab="0"
            :alarmTableValues="alarmTableValues"
            :group-filter="groupFilter"
            :org-filter="effectiveOrgFilter"
            filters-prefix="all"
        />
      </template>
    </Card>
  </div>
</template>

<script>

import MultiSelectableBreadcrumb from '@/components/ixarma/MultiSelectableBreadcrumb';
import {associateColor} from '@/service/helper/styleHelper';
import Alarms from '@/views/alarm_table/alarms/index'
import {DurationEnum} from '@/enums/duration.enum';
import {getFileLink} from "@/api/alarmTable";
import {downloadFile as saveResponseFile} from '@/views/alarm_table/alarms/utils/downloadFile'
import {readStorageJson} from '@/utils/safeJson'
import {writeStorageJson} from '@/storage/alarmFilterStorage'
import {
  createAlarmJobStatuses,
  createAlarmTableFilterKeys,
  createDefaultAlarmFilters,
  createDefaultAlarmRow,
  createPageSizeOptions,
  readAlarmTableRows,
} from '@/views/alarm_table/alarms/utils/alarmTableState'
// import LoneworkerHistory from "@/components/LoneworkerHistory.vue";
// import LoneworkerActive from "@/components/LoneworkerActive.vue";
// import Selection from '@/components/Selection.vue'
import {
  downloadExcelItems,
  downloadPdfItems,
  initialColsData
} from "@/constants/constants";
import {getOrganizationColumns, getUserColumns} from "@/api/uiColumns";
import {subscribeOrganizationContext} from "@/state/organizationContext";
import { logError } from "@/utils/appLogger";

function createOrgIdsSignature(orgIds = []) {
  return orgIds.map(({id}) => String(id)).join('|')
}

export default {
  name: 'index',
  props: {
    isTitleVisible: {
      type: Boolean,
      default: true,
    },
    isFirstSelected: {
      type: Boolean,
      default: false,
    },
    orgFilter: {
      type: [String, Number, Array, Object],
      default: null,
    }
  },
  components: {
    'MultiSelectableBreadcrumb': MultiSelectableBreadcrumb,
    'Alarms': Alarms,
    // 'LoneworkerHistory': LoneworkerHistory,
    // 'LoneworkerActive': LoneworkerActive,
    // 'Selection': Selection,
  },
  data() {
    return {
      loading: true,
      currentPage: 0,
      totalRecords: 0,
      orderByPriority: false,
      hoursDuration: 23,
      alarms: [createDefaultAlarmRow()],
      alarmTableRows: readAlarmTableRows(),
      pageSize: createPageSizeOptions(),
      alarmJobStatuses: createAlarmJobStatuses(),
      alarmTableFilterKeys: createAlarmTableFilterKeys(),
      durationFilters: {
        start: null,
        end: null,
        option: DurationEnum.RANGE,
      },
      sortKey: '',
      alarmFilterKey: '',
      alarmFilterValue: '',
      alarmFilters: createDefaultAlarmFilters(),
      groupFilter: false,
      selectedAlarmTableItem: null,
      selectedJobId: null,
      allAlarmsIntervalId: null,
      orgStorageData: '',
      orgFilterLocal: [],
      unsubscribeOrganizationContext: null,
      activeTab: this.$route.query.tab ?? '0',
      participants: [],
      downloadExcelItems: downloadExcelItems,
      downloadPdfItems: downloadPdfItems,
      alarmTableValues: [],
    }
  },
  beforeUnmount() {
    clearInterval(parseInt(this.allAlarmsIntervalId))
    this.unsubscribeOrganizationContext?.()
  },
  computed: {
    effectiveOrgFilter() {
      return this.orgFilter !== null ? this.orgFilter : this.orgFilterLocal
    },
    pageTitle() {
      switch (this.activeTab) {
        case 1:
          return this._t('menu.my_alarms');
        case 2:
          return this._t('menu_history');
        default:
          return this._t('menu.alarm_table');
      }
    }
  },
  watch: {
    activeTab(tab) {
      const sortStorageKey = this.getSortStorageKey(tab)

      this.sortKey = sortStorageKey ? readStorageJson(sortStorageKey, '') : ''
    },
    '$route.query.tab'() {
      const tab = this.$route.query.tab ?? '0';
      this.activeTab = parseInt(tab);
    },
    hoursDuration(value) {
      if (value <= 0) {
        this.hoursDuration = 23
      }
    },
    groupFilter(value) {
      this.groupFilter = value
      this.saveFilterState()
    },
    orgFilterLocal(value) {
      this.orgFilterLocal = value
      this.saveFilterState()
    }
  },
  mounted() {
    const tab = this.$route.query.tab ?? '0';
    this.activeTab = parseInt(tab);
    this.checkOrgFilter()
    this.loadFilterState()

    getUserColumns().then((userRes) => {
      if (userRes.data) {
        for (const [key, value] of Object.entries(userRes.data)) {
          if (key === 'alarmTableItems') this.alarmTableValues = value
        }
      } else {
        getOrganizationColumns().then((res) => {
          if (!res.data) {
            res.data = initialColsData
          }
          for (const [key, value] of Object.entries(res.data)) {
            if (key === 'alarmTableItems') this.alarmTableValues = value
          }
        }).catch((error) => {
          this.alarmTableValues = initialColsData.alarmTableItems
          logError('getOrganizationColumns failed', error)
        })
      }
    }).catch((error) => {
      this.alarmTableValues = initialColsData.alarmTableItems
      logError('getUserColumns failed', error)
    })
  },
  methods: {
    getSortStorageKey(tab) {
      switch (Number(tab)) {
        case 0:
          return 'af.all.sortKey'
        case 1:
          return 'af.my.sortKey'
        case 2:
          return 'af.history.sortKey'
        default:
          return null
      }
    },
    toggleSort(event) {
      this.$refs.srt.toggle(event);
    },
    toggleExcelDownload(event) {
      this.$refs.xlsDownload.toggle(event);
    },
    togglePDFDownload(event) {
      this.$refs.pdfDownload.toggle(event);
    },
    loadFilterState() {
      const sortKeyFilter = readStorageJson('af.all.sortKey', '')

      if (sortKeyFilter) this.sortKey = sortKeyFilter

      this.groupFilter = readStorageJson('af.global.groupFilter', false)
      this.orgFilterLocal = readStorageJson('af.global.orgFilter', [])
    },
    saveFilterState() {
      writeStorageJson('af.global.groupFilter', this.groupFilter)
      writeStorageJson('af.global.orgFilter', this.orgFilterLocal)
    },
    checkOrgFilter() {
      this.unsubscribeOrganizationContext?.()
      this.unsubscribeOrganizationContext = subscribeOrganizationContext(({multiOrgIds}) => {
        if (this.orgFilter !== null) return;
        const selectedOrgIds = multiOrgIds ?? []
        const signature = createOrgIdsSignature(selectedOrgIds)
        if (this.orgStorageData !== signature) {
          this.orgStorageData = signature
          this.orgFilterLocal = selectedOrgIds
        }
      });
    },
    getFile(type) {
      let request
      switch (this.activeTab) {
        case 0:
          type = '/alarmtable/' + type
          request = this.$refs.reportAll;
          break;
        case 1:
          type = '/history/' + type
          request = this.$refs.myAlarms;
          break;
        case 2:
          type = '/history/' + type
          request = this.$refs.reportHistory;
          break;
        default:
          type = '/alarmtable/' + type
          request = this.$refs.reportAll;
          break;
      }
      getFileLink(type,
          request.currentPage,
          request.alarmFilterKey.value,
          request.alarmFilterValue,
          request.alarmTableRows,
          request.alarmFilters,
          request.orgFilter,
          request.durationFilters,
          request.orderByPriority,
          request.participantsFilter,
      ).then((response) => {
        const disposition = response.headers['content-disposition']
        if (disposition && disposition.indexOf('attachment') !== -1) {
          let filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          let matches = filenameRegex.exec(disposition);
          if (matches !== null && matches[1]) {
            let filename = matches[1].replace(/['"]/g, '');
            this.downloadFile(response, filename)
          }
        }
      })
    },
    downloadFile(response, filename) {
      saveResponseFile(response, filename)
    },
    associateColor,
    switchTab() {
      if (this.$route.query.tab !== this.activeTab) {
        this.$router.push(`/alarm-table?tab=${this.activeTab}`)
      }
      if (this.activeTab === 0 && this.$refs.reportAll) {
        this.$refs.reportAll.selectedJobId = null
      }
      if (this.activeTab === 1 && this.$refs.myAlarms) {
        this.$refs.myAlarms.selectedJobId = null
      }
      if (this.activeTab === 2 && this.$refs.reportHistory) {
        this.$refs.reportHistory.selectedJobId = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.ui-sticky {
  top: 70px !important;
  position: fixed !important;
}

@media (max-width: 960px) {
  .ui-sticky {
    top: 110px !important;
  }
}

::v-deep(.p-datatable-frozen-tbody) {
  font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
  font-weight: bold;
}

::v-deep(.p-progressbar) {
  height: .5rem;
  background-color: #D8DADC;

  .p-progressbar-value {
    background-color: #607D8B;
  }
}

.p-datatable .p-column-filter {
  //display: none;
}

.table-header {
  display: flex;
  justify-content: space-between;
}

::v-deep(.p-card) {
  margin: 10px 10px 0 10px;
  padding: 0.5rem 0.5rem 0 0.5rem;

  .p-card-body {
    padding: 1rem 1rem 0 1rem;
  }

  .p-card-content {
    padding: 1rem 0 0;
  }

  .p-tabview-panels {
    padding: 1rem 1rem 0 1rem;
  }
}

::v-deep(.p-datatable) {
  .p-datatable-header {
    padding: 10px;
    text-align: left;
    font-size: 1.5rem;
  }

  .p-paginator {
    padding: 10px;
  }

  .p-datatable-thead > tr > th {
    text-align: left;
    padding: 10px;
  }

  .p-datatable-tbody > tr > td {
    cursor: auto;
    padding: 0px;
    font-weight: 1500;
  }

  .p-dropdown-label:not(.p-placeholder) {
    text-transform: uppercase;
  }
}

/* Responsive */
.p-datatable-customers .p-datatable-tbody > tr > td .p-column-title {
  display: none;
}

.customer-badge {
  border-radius: 2px;
  padding: .25em .5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: .3px;

  &.status-qualified {
    background: #C8E6C9;
    color: #256029;
  }

  &.status-unqualified {
    background: #FFCDD2;
    color: #C63737;
  }

  &.status-negotiation {
    background: #FEEDAF;
    color: #8A5340;
  }

  &.status-new {
    background: #B3E5FC;
    color: #23547B;
  }

  &.status-renewal {
    background: #ECCFFF;
    color: #694382;
  }

  &.status-proposal {
    background: #FFD8B2;
    color: #805B36;
  }
}

.p-progressbar-value.ui-widget-header {
  background: #607d8b;
}

@media (max-width: 640px) {
  .p-progressbar {
    margin-top: .5rem;
  }
}

.product-image {
  width: 100px;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)
}

.orders-subtable {
  padding: 1rem;
}

.product-badge {
  border-radius: 2px;
  padding: .25em .5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: .3px;

  &.status-instock {
    background: #C8E6C9;
    color: #256029;
  }

  &.status-outofstock {
    background: #FFCDD2;
    color: #C63737;
  }

  &.status-lowstock {
    background: #FEEDAF;
    color: #8A5340;
  }
}

.order-badge {
  border-radius: 2px;
  padding: .25em .5rem;
  text-transform: uppercase;
  font-weight: 700;
  font-size: 10px;
  letter-spacing: .3px;

  &.order-delivered {
    background: #C8E6C9;
    color: #256029;
  }

  &.order-cancelled {
    background: #FFCDD2;
    color: #C63737;
  }

  &.order-pending {
    background: #FEEDAF;
    color: #8A5340;
  }

  &.order-returned {
    background: #ECCFFF;
    color: #694382;
  }
}

@media screen and (max-width: 960px) {
  ::v-deep(.p-datatable) {
    &.p-datatable-customers {
      .p-datatable-thead > tr > th,
      .p-datatable-tfoot > tr > td {
        display: none !important;

      }

      .p-datatable-tbody > tr {
        border-bottom: 1px solid white;

        > td {
          text-align: left !important;
          display: block;
          border: 0 none !important;
          width: 100% !important;
          float: left;
          clear: left;
          border: 0 none;

          .p-column-title {

            min-width: 30%;
            display: inline-block;
            margin: -.4rem 1rem -.4rem -.4rem;
            font-weight: bold;
          }

          .p-progressbar {
            margin-top: .5rem;
            display: inline-block;
            width: 60%;
          }

          .p-rating {
            display: inline-block;
          }
        }
      }

      .p-datatable-tbody > tr.p-rowgroup-footer {
        display: flex;
      }
    }
  }
}

.true-icon {
  color: #256029;
}

.false-icon {
  color: #C63737;
}

::v-deep(.p-treetable) {
  .p-treetable-tbody > tr > td {
    padding: 5px;
  }

  .p-treetable-thead > tr > th {
    padding: 5px;
  }

}


.customer {
  font-family: Arial, Helvetica, sans-serif;
  border-collapse: collapse;
  width: 100%;
}

.customer td, .customers th {
  border: none;
  padding: 5px;
}

.customer tr:nth-child(even) {
  //background-color: #f2f2f2;
}

.truncate {
  min-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.disable-th th {
  display: none;
}

p-tabview-nav-link {
  color: #82B541;
  border-color: #82B541;
}

.p-highlight {
  background-color: #82B541 !important;
}

.custom-single-view-filter {
  width: 30% !important;
  margin: 5px !important;
}

::v-deep(.embedded-alarm-table.p-card) {
  margin: 5px;
  padding: 5px;

  .p-card-body {
    padding: 0;
  }

  .p-card-content {
    padding: 0;
  }
}
</style>
